import { ModuleType } from '@fi-shared/types/module-type';
import { AccessRightFilterSettings } from '@fi-shared/access-right-filters';
import {
    Component,
    OnInit,
    Input,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ViewChild,
    AfterViewInit,
    QueryList,
    ViewChildren,
    Injector,
} from '@angular/core';
import { IFormEdit } from '@fi-shared/fi-form-shared/fi-form-shared';
import { ObjectTypes } from '@fi-shared/types';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AccessRightFiltersComponent } from '@fi-shared/access-right-filters/access-right-filters.component';
import { first } from 'rxjs/operators';
import { YesNoPipe } from '@fi-app/utilities/fi-pipes/yes-no.pipe';
import { AppTextService } from '@fi-shared/app-text';
import { FiPageCollapsableContentComponent } from '@fi-app/components/fi-page-collapsable-content/fi-page-collapsable-content.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiUserContactTypeEnum } from '@fi-shared/fi-user-contact-chip-grid-input/fi-user-contact-type.enum';
import { isDefined } from '@fi-shared/utils';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';

@Component({
    selector: 'fi-form-edit-settings',
    templateUrl: './fi-form-edit-settings.component.html',
    styleUrls: ['./fi-form-edit-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiFormEditSettingsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiFormEditSettingsComponent,
        },
    ],
    host: { class: 'fi-form-edit-settings' },
    standalone: false,
})
export class FiFormEditSettingsComponent
    extends ControlValueAccessorValidatorBase<IFormEdit>
    implements OnInit, AfterViewInit
{
    @Input() isTooltipHidden: boolean;

    @ViewChild(AccessRightFiltersComponent, { static: false }) accessRightFilters: AccessRightFiltersComponent;
    @ViewChildren(FiPageCollapsableContentComponent) rollupContents: QueryList<FiPageCollapsableContentComponent>;

    accessRightFilterSettings: AccessRightFilterSettings;
    ObjectTypes = ObjectTypes;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly FiUserContactTypeEnum = FiUserContactTypeEnum;

    sharingUserEmails: string[] = [];
    UserData: INameIdObj[] = [];
    selectedSelectionTitle: string;

    constructor(
        injector: Injector,
        private yesNoPipe: YesNoPipe,
        private appTextService: AppTextService,
    ) {
        super(injector);
    }

    get form(): IFormEdit {
        return this.ngModel;
    }

    ngOnInit(): void {
        if (!this.form.hasOwnProperty('Id')) this.form.IsShowInToDo = true;

        this.accessRightFilterSettings = new AccessRightFilterSettings(ModuleType.Form, false, true, ObjectTypes.Form);
        this.updateSharingUserEmails();
    }

    ngAfterViewInit(): void {
        this.accessRightFilters.onInitialized$.pipe(first()).subscribe(() => {
            this.UserData = this.accessRightFilters.UserData.Data.map((item) => ({
                Id: item.Id,
                Name: item.Name,
            }));
        });

        if (this.rollupContents) this.rollupContents.forEach((rollupContent) => rollupContent.expandContent());
    }

    private updateSharingUserEmails(): void {
        this.sharingUserEmails = this.parseUserEmails(this.form.UserEmailsToSendSubmissions);
    }

    private parseUserEmails(userEmails: string): string[] {
        if (!isDefined(userEmails) || !userEmails.trim()) return [];
        return userEmails
            .split(',')
            .map((emailStr) => emailStr.trim())
            .filter(Boolean);
    }

    protected onUserEmailsChange(): void {
        this.form.UserEmailsToSendSubmissions = this.sharingUserEmails.join(', ');
    }

    protected onIsMultipleAnswerChange($event: MatCheckboxChange): void {
        if ($event.checked) this.form.IsShowInToDo = false;
    }

    protected onIsAnonymousAnswersChange($event: MatCheckboxChange): void {
        if ($event.checked) this.form.IsMultipleAnswer = true;
    }

    protected isMultipleAnswerDisabled(): boolean {
        return this.form.IsAnonymousAnswers;
    }

    protected getShareText(): string {
        if (!this.form.PublicationSetting) return '';

        const result = this.yesNoPipe.transform(this.form.PublicationSetting.IsShared);

        if (!this.form.PublicationSetting.IsShared) return result;

        return result + ', ' + this.getFormPublicationLabel().toLowerCase();
    }

    private getFormPublicationLabel(): string {
        return this.appTextService.get(
            this.form.PublicationSetting.PublicationType === 1 ? 'common.continuous' : 'common.time_limited',
        );
    }

    protected updateSelectedSelectionTitle(selectionTitle: string) {
        this.selectedSelectionTitle = selectionTitle;
    }
}
